import React from 'react';

import isEmpty from 'lodash/isEmpty';

import { useClassnames } from '@lumapps/classnames';
import { FormDialog, FormDialogProps } from '@lumapps/lumx-forms/components/FormDialog';
import { GridLayout, Section } from '@lumapps/lumx-layouts/components/GridLayout';
import { Heading } from '@lumapps/lumx/react';
import type { TranslatableObject } from '@lumapps/marketplace/sdk';
import { InputVariableSource } from '@lumapps/micro-apps-builder/constants';
import type { InputVariableDefinition } from '@lumapps/micro-apps-builder/types';
import { LumAppsComponentSettings } from '@lumapps/micro-apps-integrations/components/LumAppsComponentSettings';
import type { LumappsComponentProperties, MicroAppsInputVariables } from '@lumapps/micro-apps/types';
import { useTranslate } from '@lumapps/translations';

import { useNavigation } from '../../hooks/useNavigation';
import { useTabErrors } from '../../hooks/useTabErrors';
import { MICRO_APP_PICKER } from '../../keys';
import { InputVariablesSetup } from '../InputVariablesSetup';
import { SetupFormNavigation } from '../SetupFormNavigation';

import './index.scss';

export interface SetupFormDialogProps extends Omit<FormDialogProps, 'heading' | 'scope'> {
    /** Properties for LumApps components used by the micro-app. */
    componentSettings: LumappsComponentProperties[];
    /** The translatable name of the micro-app extension. */
    extensionName: TranslatableObject;
    /** The current values of the input variables. */
    inputVariables?: MicroAppsInputVariables;
    /** Definitions for the micro-app's input variables (both system and developer-defined). */
    inputVariablesDefinitions?: InputVariableDefinition[];
    /** A ref to the element that triggered the dialog, used for positioning. */
    parentElement?: React.RefObject<HTMLElement>;
}

/** The class name for the component. */
const CLASSNAME = 'setup-form-dialog';
/**
 * A dialog component for configuring a micro-app's settings and input variables.
 * It can display two sections: one for system-level settings (e.g., for integrated components)
 * and another for developer-defined input variables. If both sections are present, it provides
 * navigation between them.
 *
 * @param props The properties for the component.
 * @returns A `FormDialog` for setting up the micro-app.
 */
const SetupFormDialog: React.FC<SetupFormDialogProps> = ({
    componentSettings,
    extensionName,
    inputVariables = {},
    inputVariablesDefinitions,
    isOpen,
    parentElement,
    onCancel,
    onClose,
    onSubmit,
}) => {
    const { block, element } = useClassnames(CLASSNAME);
    const { translateKey, translateObject } = useTranslate();
    const { onErrorChange, tabErrors } = useTabErrors();
    const { activeMenuKey, menus, getTranslatedKey, onMenuPress } = useNavigation('settings');

    /**
     * Memoized hook that processes input variable definitions.
     * It injects the current values as defaults and splits them into system and developer-defined variables.
     */
    const { developerInputVariablesDefinitions, systemInputVariablesDefinitions } = React.useMemo(() => {
        const developerInputVariablesDefinitions = inputVariablesDefinitions?.filter(
            (input) => input.source === InputVariableSource.DEVELOPER,
        );
        const systemInputVariablesDefinitions = inputVariablesDefinitions?.filter(
            (input) => input.source === InputVariableSource.SYSTEM,
        );

        return {
            developerInputVariablesDefinitions,
            systemInputVariablesDefinitions,
        };
    }, [inputVariablesDefinitions]);

    const hasNavSection =
        systemInputVariablesDefinitions &&
        systemInputVariablesDefinitions.length > 0 &&
        developerInputVariablesDefinitions &&
        developerInputVariablesDefinitions.length > 0;

    /**
     * Memoized hook that prepares the initial form values based on the default values
     * of the developer and system input variables.
     */
    const formValues = React.useMemo(
        () =>
            inputVariablesDefinitions?.reduce(
                (acc, input) => ({
                    ...acc,
                    [input.id]: inputVariables[input.id] ?? input.defaultValue,
                }),
                {},
            ),
        [inputVariables, inputVariablesDefinitions],
    );

    /**
     * Effect to automatically switch to the 'inputVariables' tab if no 'settings' (system variables)
     * tab is present. This ensures a valid tab is always selected on mount.
     */
    React.useEffect(() => {
        if (systemInputVariablesDefinitions === undefined || systemInputVariablesDefinitions.length === 0) {
            onMenuPress('inputVariables');
        }
    }, [systemInputVariablesDefinitions, onMenuPress]);

    return (
        <FormDialog
            className={block({ hideNav: !hasNavSection })}
            dialogProps={{ 'aria-labelledby': 'microapp-setup-dialog', parentElement, preventAutoClose: true }}
            form={{ values: formValues }}
            heading={
                <Heading id="microapp-setup-dialog" as="h3">{`${translateKey(
                    MICRO_APP_PICKER.SETUP_MICROAPP,
                )} - ${translateObject(extensionName)}`}</Heading>
            }
            isOpen={isOpen}
            scope={CLASSNAME}
            onCancel={onCancel}
            onClose={onClose}
            onSubmit={onSubmit}
        >
            <GridLayout>
                {hasNavSection && (
                    <Section position="left" elementAs="nav">
                        <SetupFormNavigation
                            activeMenuKey={activeMenuKey}
                            errors={tabErrors}
                            menus={menus}
                            onMenuPress={onMenuPress}
                        />
                    </Section>
                )}

                <Section position="middle" className={element('middle')}>
                    <Heading as="h4">{getTranslatedKey(activeMenuKey || 'settings')}</Heading>

                    {systemInputVariablesDefinitions && activeMenuKey === 'settings' && !isEmpty(componentSettings) && (
                        <LumAppsComponentSettings
                            componentsProperties={componentSettings}
                            inputVariables={inputVariables}
                        />
                    )}

                    {developerInputVariablesDefinitions && activeMenuKey === 'inputVariables' && (
                        <InputVariablesSetup
                            inputVariablesDefinitions={developerInputVariablesDefinitions}
                            isDisplayed={activeMenuKey === 'inputVariables'}
                            onErrorChange={onErrorChange('inputVariables')}
                        />
                    )}
                </Section>
            </GridLayout>
        </FormDialog>
    );
};

export default SetupFormDialog;
